import React, { useEffect, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/20/solid";
import { Menu, Transition } from "@headlessui/react";
import moment from "moment";
import ShowInfoAfspraak from "./ShowInfoAfspraak";
import {
  classNames,
  generateDaysArrayForMonth,
} from "./calenderUtilties";
import CrudEventCalendly from "./CrudEventCalendly";
import CrudEventCalendlyPatienten from "./CrudEventCalendlyPatienten";
import { useDispatch } from "react-redux";
import { setPagination } from "../../redux/appointmentSlice";


// Title array for days of the week
const titleDayArray = [
  { full: "Maandag", short: "Ma" },
  { full: "Dinsdag", short: "Di" },
  { full: "Woensdag", short: "Wo" },
  { full: "Donderdag", short: "Do" },
  { full: "Vrijdag", short: "Vr" },
  { full: "Zaterdag", short: "Za" },
  { full: "Zondag", short: "Zo" },
];

const Header = ({ currentDate, onPrevious, onNext, setOpen }) => (
  <header className="flex flex-wrap items-center justify-between gap-3 border-b border-wa-border bg-wa-panel px-3 py-3 sm:px-5 lg:flex-none">
    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
      <button
        onClick={onPrevious}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-wa-muted transition-colors hover:bg-wa-hover hover:text-wa-text"
      >
        <span className="sr-only">Previous month</span>
        <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
      </button>
      <h1 className="text-base font-semibold capitalize leading-6 tracking-wide text-wa-text sm:text-lg">
        <time>{moment(currentDate).locale("nl-be").format("MMMM YYYY")}</time>
      </h1>
      <button
        onClick={onNext}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-wa-muted transition-colors hover:bg-wa-hover hover:text-wa-text"
      >
        <span className="sr-only">Next month</span>
        <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
      </button>
    </div>
    <ButtonGroup setOpen={setOpen} />
  </header>
);

const ButtonGroup = ({ setOpen }) => (
  <div className="flex items-center">
    <button
      onClick={() => setOpen(true)}
      className="ml-auto rounded-lg bg-wa-accent px-3 py-2 text-sm font-semibold text-wa-onaccent shadow-sm transition hover:brightness-110 sm:ml-3"
    >
      Afspraak maken
    </button>
    {/* Add Menu for additional actions */}
    <Menu as="div" className="relative ml-3 sm:ml-6">
      <Menu.Button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-wa-muted transition-colors hover:bg-wa-hover hover:text-wa-text">
        <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
      <Transition as={React.Fragment}>
        <Menu.Items className="absolute right-0 z-10 mt-3 w-36 rounded-md border border-wa-border bg-wa-panel2 shadow-lg">
          {/* Add Menu Items here for "Day View", "Week View", etc. */}
        </Menu.Items>
      </Transition>
    </Menu>
  </div>
);

const DaysGrid = ({ days, showAfspraak }) => (
  <div className="flex text-xs lg:flex-auto">
    <div className="w-full grid grid-cols-7">
      {days.map((day) => (
        <Day key={day.date} day={day} showAfspraak={showAfspraak} />
      ))}
    </div>
  </div>
);

const Day = ({ day, showAfspraak }) => {
  const weekday = moment(day.date).isoWeekday();
  const isWeekend = weekday === 6 || weekday === 7;

  return (
    <div
      className={classNames(
        "relative min-h-[100px] border-t border-r border-wa-border p-1.5 transition-colors sm:min-h-[120px] sm:p-2.5 lg:min-h-[130px]",
        day.isCurrentMonth
          ? isWeekend
            ? "bg-wa-panel2/90 text-wa-text"
            : "bg-wa-panel2 text-wa-text"
          : "bg-wa-panel text-wa-muted/70",
        day.isToday ? "ring-1 ring-inset ring-wa-accent/45" : "hover:bg-wa-rowhover"
      )}
    >
      <time
        dateTime={day.date}
        className={classNames(
          "inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold",
          day.isToday ? "bg-wa-accent text-wa-onaccent" : "text-current"
        )}
      >
        {day.date.split("-").pop().replace(/^0/, "")}
      </time>
      {day?.events?.length > 0 && (
        <ol className="mt-2 space-y-1">
          {day?.events.slice(0, 3).map((event, eventIndex) => (
            <li
              key={
                event?.uri ||
                event?.uuid ||
                event?.id ||
                `${event?.start_time || "no-start"}-${event?.name || "no-name"}-${eventIndex}`
              }
              title={event?.admin_accept == 0 ? "Moet bevestigd worden" : ""}
              className={classNames(
                "mt-1 cursor-pointer rounded px-1.5 py-1 text-white transition-opacity hover:opacity-90",
                event.color || "bg-wa-accent"
              )}
              onClick={() => showAfspraak(event)}
            >
              <a className="group flex items-center">
                <p className="truncate text-[10px] font-semibold sm:text-xs">{event?.name}</p>
                <time
                  dateTime={event?.start_time}
                  className="ml-2 hidden text-[11px] font-medium opacity-90 xl:block"
                >
                  {moment(event.start_time).format("HH:mm")}
                </time>
              </a>
            </li>
          ))}
          {day?.events?.length > 3 && (
            <li className="px-1 text-[10px] font-medium text-wa-muted sm:text-[11px]">+ {day?.events?.length - 3} meer</li>
          )}
        </ol>
      )}
    </div>
  );
};

const MonthView = ({ user, userdetails, select, appointments }) => {
  const [currentDate, setCurrentDate] = useState(
    moment().locale("nl-be").format("YYYY-MM")
  );
  const [days, setDays] = useState(
    generateDaysArrayForMonth(currentDate, null, appointments)
  );

  const dispatch = useDispatch();



  const [open, setOpen] = useState(false);
  const [afspraak, setAfspraak] = useState(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);

  const showAfspraak = (afspr) => {
    setAfspraak(afspr);
    setOpenModalEdit(true);
  };

  useEffect(() => {
    setDays(generateDaysArrayForMonth(currentDate, null, appointments));
  }, [currentDate, appointments]);

  const handlePreviousMonth = () =>{
    setCurrentDate(moment(currentDate).subtract(1, "month").format("YYYY-MM"));
    dispatch(setPagination(moment(currentDate).subtract(1, "month").startOf('month').toISOString()));
  };
  
  const handleNextMonth = () => {
    setCurrentDate(moment(currentDate).add(1, "month").format("YYYY-MM"));
    dispatch(setPagination(moment(currentDate).add(1, "month").startOf('month').toISOString()));
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-wa-border bg-wa-panel2 shadow-lg shadow-black/10 lg:flex lg:flex-col">
      <Header
        currentDate={currentDate}
        onPrevious={handlePreviousMonth}
        onNext={handleNextMonth}
        setOpen={setOpen}
      />
      <div className="ring-1 ring-wa-border lg:flex lg:flex-auto lg:flex-col">
        <div className="grid grid-cols-7 border-b border-wa-border bg-wa-panel text-center text-[10px] font-semibold uppercase text-wa-muted sm:text-xs">
          {titleDayArray.map((title, key) => (
            <div
              key={key}
              className="truncate border-r border-wa-border px-1 py-2 last:border-r-0 sm:px-2 sm:py-2.5"
            >
              <span className="sm:hidden">{title.short}</span>
              <span className="hidden sm:inline">{title.full}</span>
            </div>
          ))}
        </div>
        <DaysGrid days={days} showAfspraak={showAfspraak} />
      </div>
      {user?.role == 2 ? (
        <CrudEventCalendly openModel={open} setOpenModel={setOpen} />
      ) : (
        <CrudEventCalendlyPatienten openModel={open} setOpenModel={setOpen} />
      )}

      {afspraak && (
        <ShowInfoAfspraak
          open={openModalEdit}
          setOpen={setOpenModalEdit}
          select={select}
          evenment={afspraak}
          user={user}
        />
      )}
    </div>
  );
};

export default MonthView;
